import { useRef, type ReactNode } from 'react';
import { CalendarOutlined, PictureOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import WaterImage from '@/components/WaterImage';

// Etkinlik/Basın kartı — kapak görseli + başlık + tarih + özet.
// href verilirse kart DIŞ bağlantıya (yeni sekme) gider; verilmezse site içi
// detay sayfasına (to). Hover'da fareyi takip eden 3D tilt efekti.
const ContentCard = ({
  to,
  href,
  image,
  title,
  date,
  excerpt,
  tag,
  overlay = false,
}: {
  to: string;
  /** Dış bağlantı — varsa kart iç detay yerine bu adrese gider. */
  href?: string;
  image?: string;
  title: string;
  date?: string;
  excerpt?: string;
  tag?: string;
  /** Metni görselin altına degrade üzerine bindirir (büyük "hero" kart). */
  overlay?: boolean;
}) => {
  const ref = useRef<HTMLAnchorElement>(null);

  const onMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${(-py * 6).toFixed(
      2,
    )}deg) rotateY(${(px * 8).toFixed(2)}deg) translateY(-5px) scale(1.02)`;
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = '';
  };

  // Aynı görünüm, iki farklı bağlantı türü.
  const wrap = (className: string, children: ReactNode) =>
    href ? (
      <a
        ref={ref}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className={className}
      >
        {children}
      </a>
    ) : (
      <Link
        ref={ref}
        to={to}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className={className}
      >
        {children}
      </Link>
    );

  // Büyük, metni görselin üzerine bindiren varyant (referans tasarım).
  if (overlay) {
    return wrap(
      'group relative block aspect-[3/4] overflow-hidden rounded-3xl bg-slate-900 shadow-lg ring-1 ring-black/5 transition-transform duration-200 ease-out will-change-transform hover:shadow-2xl dark:ring-white/10',
      <>
        {image ? (
          <WaterImage
            src={image}
            className="absolute inset-0 h-full w-full"
            imgClassName="object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-5xl text-white/20">
            <PictureOutlined />
          </div>
        )}
        {/* Okunabilirlik için alttan koyu degrade */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent"
        />
        {tag && (
          <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-brand-600 backdrop-blur dark:bg-black/50 dark:text-accent">
            {tag}
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
          {date && (
            <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-white/70">
              <CalendarOutlined className="text-[11px]" />
              {date}
            </div>
          )}
          <h3 className="line-clamp-2 text-lg font-bold leading-snug text-white sm:text-xl">
            {title}
          </h3>
          {excerpt && (
            <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-white/75">
              {excerpt}
            </p>
          )}
        </div>
      </>,
    );
  }

  return wrap(
    'group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-transform duration-200 ease-out will-change-transform hover:shadow-2xl dark:border-white/10 dark:bg-white/[0.04]',
    <>
      <div className="relative aspect-[4/5] overflow-hidden bg-slate-100 dark:bg-white/5">
        {image ? (
          <img
            src={image}
            alt=""
            referrerPolicy="no-referrer"
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-3xl text-slate-300 dark:text-slate-600">
            <PictureOutlined />
          </div>
        )}
        {tag && (
          <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-brand-600 backdrop-blur dark:bg-black/50 dark:text-accent">
            {tag}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4">
        {date && (
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] text-slate-400">
            <CalendarOutlined className="text-[10px]" />
            {date}
          </div>
        )}
        <h3 className="line-clamp-2 text-sm font-semibold text-slate-900 transition-colors group-hover:text-brand-600 dark:text-white">
          {title}
        </h3>
        {excerpt && (
          <p className="mt-1.5 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">
            {excerpt}
          </p>
        )}
      </div>
    </>,
  );
};

export default ContentCard;
